/**
 * Add League Modal
 *
 * Lets users connect their fantasy league from ESPN, Sleeper, or Yahoo.
 * Each platform connects differently: Sleeper needs only a username (its API
 * is public), Yahoo uses real OAuth, and ESPN — which has neither an API nor
 * OAuth — needs the two login cookies ESPN itself set in the user's browser.
 *
 * ESPN therefore has two routes in, and this modal offers whichever fits:
 *   1. The JAI browser extension. One click. It's the only thing that can read
 *      `espn_s2`, which is HttpOnly and so invisible to this page's JavaScript.
 *   2. Manual paste, for anyone without the extension. Slower, but works
 *      everywhere with nothing installed.
 * See extension/README.md and routes/espnConnect.js.
 */

import { useState, useEffect } from 'react'
import { leaguesApi, authApi } from '../services/api'
import { JAI_COLORS, JAI_FONTS, JaiMark } from './JaiBrand'
import { nativeSafeErrorMessage, IS_NATIVE_APP } from '../services/runtime'
import NativeEspnHandoff from './NativeEspnHandoff'

// ESPN's subtitle mentions the browser extension and manual paste — neither
// exists on native, so it gets its own copy there (see the handoff screen,
// NativeEspnHandoff.jsx, for the full explanation shown once ESPN is picked).
const PLATFORMS = [
  {
    id: 'espn', label: 'ESPN',
    description: IS_NATIVE_APP ? 'Connects from a computer' : 'Connect with the JAI extension, or paste your login values',
  },
  { id: 'sleeper', label: 'Sleeper', description: 'Just paste your league ID' },
  { id: 'yahoo',   label: 'Yahoo',   description: 'Sign in with your Yahoo account' },
]

// What went wrong inside the extension, in words a user can act on.
const EXTENSION_ERRORS = {
  not_logged_in:      'You’re not signed in to ESPN in this browser. Sign in at fantasy.espn.com, then click Connect again — or use the manual option below.',
  no_extension:       'The JAI extension didn’t respond. If you just installed it, reload this page and try again.',
  extension_reloaded: 'The extension restarted mid-connect. Reload this page and try again.',
  network:            'The extension couldn’t reach JAI. Check your connection and try again.',
  bad_origin:         'The extension refused to send credentials from this address. Contact support if this persists.',
  rejected:           'JAI didn’t accept the connection. Click “start over” below for a fresh attempt.',
}

/**
 * One line saying what the extension could and couldn't see, appended to the
 * error so a screenshot from a user who says "it doesn't work" already holds
 * the diagnosis. Absent on older extension versions (they send no `diag`).
 */
function extensionDetail(d) {
  const bits = []
  if (d.diag) {
    if (d.diag.api != null) bits.push(`browser sees ${d.diag.api} ESPN cookies`)
    if (d.diag.pageAnswered != null) bits.push(d.diag.pageAnswered ? 'ESPN page replied without them' : 'ESPN page did not reply')
    if (d.diag.net != null) bits.push(`${d.diag.net} ESPN requests watched`)
  }
  if (d.status) bits.push(`server said ${d.status}`)
  if (d.version) bits.push(`extension v${d.version}`)
  return bits.length ? ` (${bits.join('; ')})` : ''
}

export default function AddLeagueModal({ onClose, onAdded }) {
  const [platform, setPlatform]         = useState('espn')
  // No sport picker: the backend asks the platform which sport the league
  // actually is and records that, so there's nothing here for a user to get
  // wrong. See createLeague in leagueController.js.

  // ESPN fields
  const [espnLeagueId, setEspnLeagueId] = useState('')
  const [espnToken, setEspnToken]         = useState(null)
  const [espnCreds, setEspnCreds]         = useState(null) // { espnS2, swid } once connected
  const [espnSource, setEspnSource]       = useState(null)   // 'extension' | 'manual'
  const [espnStep, setEspnStep]           = useState('idle') // 'idle' | 'choose' | 'waiting' | 'ready' | 'expired'
  const [espnExtension, setEspnExtension] = useState(null)   // installed version, or null
  const [espnExtensionUrl, setEspnExtensionUrl] = useState(null) // store listing, once published
  // Manual setup starts collapsed only when there's a one-click path competing
  // for attention. With no extension available it's the only way in, so it
  // opens on its own rather than hiding behind a link.
  const [espnManualOpen, setEspnManualOpen] = useState(false)
  const [manualS2, setManualS2]     = useState('')
  const [manualSwid, setManualSwid] = useState('')

  // Sleeper fields
  const [sleeperLeagueId, setSleeperLeagueId] = useState('')
  const [sleeperUsername, setSleeperUsername] = useState('')

  // Yahoo — OAuth-based, no manual ID entry
  const [yahooLeagues, setYahooLeagues]   = useState([])   // fetched after OAuth
  const [yahooSelected, setYahooSelected] = useState(null) // which league user picked
  const [yahooTokens, setYahooTokens]     = useState(null) // { accessToken, refreshToken, tokenExpiry }
  const [yahooStep, setYahooStep]         = useState('connect') // 'connect' | 'pick'
  const [yahooLoading, setYahooLoading]   = useState(false)
  const [yahooReady, setYahooReady]       = useState(null)  // null = still asking, false = not enabled yet

  // Common
  // Starts blank on purpose — there's no sport picker (see above), so this
  // modal can't tell whether "August" means the NBA season has rolled to
  // next year or the NFL season hasn't. Pre-filling with the raw calendar
  // year got that wrong for whichever sport wasn't the guess, and users
  // rarely touch a field that already looks filled in — the wrong value
  // shipped as-is more often than not. Left blank, the backend resolves the
  // right season for the sport it confirms with the platform itself (see
  // createLeague in leagueController.js).
  const [season, setSeason] = useState('')
  const [error, setError]   = useState('')
  const [loading, setLoading] = useState(false)

  // ── Handle Yahoo OAuth callback ──────────────────────────────────
  // When Yahoo redirects back with ?yahoo_pending=..., this effect fires.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const pendingCode  = params.get('yahoo_pending')
    const yahooError   = params.get('yahoo_error')

    if (yahooError) {
      setError(`Yahoo login failed: ${yahooError}`)
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname)
      return
    }

    if (pendingCode) {
      // Yahoo OAuth completed — fetch the league list
      setYahooLoading(true)
      setPlatform('yahoo')
      setYahooStep('pick')
      authApi.getYahooLeagues(pendingCode)
        .then(res => {
          const leagues = res.data.leagues || []
          setYahooLeagues(leagues)
          // Only one league to pick from isn't really a choice — select it so
          // "Connect League" is live immediately. Without this the button sits
          // silently disabled (no distinct disabled style) until the user
          // clicks the single row, which reads as the button doing nothing.
          if (leagues.length === 1) setYahooSelected(leagues[0])
          setYahooTokens({
            accessToken:  res.data.accessToken,
            refreshToken: res.data.refreshToken,
            tokenExpiry:  res.data.tokenExpiry,
          })
          // Clean up the URL so a page refresh doesn't re-trigger this
          window.history.replaceState({}, '', window.location.pathname)
        })
        .catch(err => {
          setError(err.response?.data?.error || 'Failed to load Yahoo leagues')
        })
        .finally(() => setYahooLoading(false))
    }
  }, [])

  // ── Is the JAI extension installed? ──────────────────────────────
  // The extension's content script stamps its version onto <html> as soon as
  // the page loads. A data attribute is the handoff because the extension and
  // this page run in separate JavaScript worlds and can't share variables —
  // but they do share the DOM. Re-checked whenever the ESPN tab opens, so
  // switching tabs picks up an install done in another window.
  useEffect(() => {
    if (platform !== 'espn') return
    setEspnExtension(document.documentElement.dataset.jaiEspnExtension || null)
  }, [platform])

  // ── ESPN connect: get a token ────────────────────────────────────
  // Fetched as soon as the ESPN tab shows so it's ready the moment the user
  // clicks Connect. The token expires after 10 minutes, which is why it's tied
  // to opening the tab rather than to mounting the modal.
  useEffect(() => {
    if (platform !== 'espn' || espnToken) return
    let cancelled = false
    authApi.startEspnConnect()
      .then(res => {
        if (cancelled) return
        setEspnToken(res.data.token)
        setEspnExtensionUrl(res.data.extensionUrl || null)
        setEspnStep('choose')
        // Nothing one-click to offer — don't make them hunt for the only
        // option there is.
        if (!res.data.extensionUrl && !document.documentElement.dataset.jaiEspnExtension) {
          setEspnManualOpen(true)
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not start the ESPN connection. Please try again.')
      })
    return () => { cancelled = true }
  }, [platform, espnToken])

  // Polls while the extension is off reading cookies. Only runs while the ESPN
  // tab is actually showing — switching away and back resumes it cleanly since
  // the interval is recreated from scratch each time this effect re-fires.
  useEffect(() => {
    if (platform !== 'espn' || espnStep !== 'waiting' || !espnToken) return
    const id = setInterval(async () => {
      try {
        const res = await authApi.getEspnConnectStatus(espnToken)
        if (res.data.status === 'connected') {
          setEspnCreds({ espnS2: res.data.espnS2, swid: res.data.swid })
          setEspnSource('extension')
          setEspnStep('ready')
        }
      } catch (err) {
        // Only give up on a real "token gone" response — a transient network
        // hiccup shouldn't kill the flow, the next poll a moment later will do.
        if (err.response?.status === 404) {
          setEspnStep('expired')
          setError('This connection attempt expired. Click "start over" below to get a new link.')
        }
      }
    }, 2500)
    return () => clearInterval(id)
  }, [platform, espnStep, espnToken])

  // ── Connect via the extension ────────────────────────────────────
  // This page can't read ESPN's cookies and never sees them. It posts the
  // connect token into its own window; the extension's content script is
  // listening, passes it to the extension's background worker, and that worker
  // sends the cookies straight to JAI's backend. All we get back is yes or no —
  // then the poll above picks up the result.
  const handleExtensionConnect = () => {
    if (!espnToken) return
    setError('')

    const requestId = Math.random().toString(36).slice(2)
    let settled = false

    const finish = (reason, detail = {}) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      window.removeEventListener('message', onResult)
      if (reason) {
        setEspnStep('choose')
        // A failed one-click attempt must never be a dead end: open the manual
        // route so there is always a next step on screen.
        setEspnManualOpen(true)
        // Say what JAI's server actually answered when it refused (an expired
        // link reads very differently from a malformed cookie), rather than
        // one generic line for every rejection.
        const base = reason === 'rejected' && detail.error
          ? `${detail.error}`
          : EXTENSION_ERRORS[reason] || EXTENSION_ERRORS.rejected
        setError(base + extensionDetail({ ...detail, version: document.documentElement.dataset.jaiEspnExtension }))
      }
    }

    const onResult = (event) => {
      if (event.source !== window) return
      const d = event.data
      if (!d || d.source !== 'jai-extension' || d.type !== 'JAI_ESPN_RESULT') return
      if (d.requestId !== requestId) return
      finish(d.ok ? null : d.reason, d)
    }

    // If nothing answers, the extension isn't really there — most often because
    // it was installed after this page loaded, so its content script never ran.
    const timer = setTimeout(() => finish('no_extension'), 12000)

    window.addEventListener('message', onResult)
    setEspnStep('waiting')
    window.postMessage(
      { source: 'jai-page', type: 'JAI_ESPN_CONNECT', token: espnToken, requestId },
      window.location.origin
    )
  }

  // ── Connect by pasting the values by hand ────────────────────────
  // Nothing to verify here: ESPN is the only thing that can say whether these
  // are good, and it gets asked when the league is created a moment later.
  const handleManualConnect = () => {
    const s2 = manualS2.trim()
    const swid = manualSwid.trim()
    if (!s2 || !swid) {
      setError('Paste both values — espn_s2 and SWID.')
      return
    }
    setError('')
    setEspnCreds({ espnS2: s2, swid })
    setEspnSource('manual')
    setEspnStep('ready')
  }

  const restartEspnConnect = () => {
    setError('')
    setEspnToken(null)
    setEspnCreds(null)
    setEspnSource(null)
    setManualS2('')
    setManualSwid('')
    setEspnStep('idle')
  }

  // Yahoo has to be switched on per deployment (see routes/yahoo.js), so ask
  // before showing the connect button — otherwise the only way to find out it
  // isn't available is to click and get an error. Checked when the tab opens
  // rather than once on mount, so it costs nothing for the other platforms.
  useEffect(() => {
    if (platform !== 'yahoo' || yahooReady !== null) return
    let cancelled = false
    authApi.getYahooStatus()
      .then(res => { if (!cancelled) setYahooReady(Boolean(res.data.configured)) })
      .catch(() => { if (!cancelled) setYahooReady(false) })
    return () => { cancelled = true }
  }, [platform, yahooReady])

  // ── Connect Yahoo (start OAuth flow) ────────────────────────────
  const handleYahooConnect = async () => {
    setYahooLoading(true)
    setError('')
    try {
      const res = await authApi.startYahooOAuth()
      // Redirect the user to Yahoo's authorization page
      window.location.href = res.data.authUrl
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start Yahoo login. Check YAHOO_CLIENT_ID in .env.')
      setYahooLoading(false)
    }
  }

  // ── Form submit ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Omit season entirely when the user left it blank — sending a parsed
      // NaN/0 would defeat the backend's own sport-aware default (see the
      // comment on the `season` state above).
      let payload = { platform, ...(season ? { season: parseInt(season) } : {}) }

      if (platform === 'espn') {
        if (espnStep !== 'ready' || !espnCreds) {
          setError('Connect your ESPN account first.')
          setLoading(false)
          return
        }
        payload = { ...payload, espnLeagueId, espnS2: espnCreds.espnS2, swid: espnCreds.swid }
      } else if (platform === 'sleeper') {
        payload = { ...payload, sleeperLeagueId, sleeperUsername }
      } else if (platform === 'yahoo') {
        if (!yahooSelected) {
          setError('Select a Yahoo league first.')
          setLoading(false)
          return
        }
        payload = {
          ...payload,
          yahooLeagueKey:   yahooSelected.leagueKey,
          season:           yahooSelected.season || season,
          yahooAccessToken:  yahooTokens?.accessToken,
          yahooRefreshToken: yahooTokens?.refreshToken,
          yahooTokenExpiry:  yahooTokens?.tokenExpiry,
        }
      }

      const res = await leaguesApi.create(payload)
      onAdded(res.data.league)
    } catch (err) {
      // The league-slot limit (requireLeagueSlot) returns
      // PLAN_UPGRADE_REQUIRED with "...upgrade to connect more" — not
      // allowed to render on native (Guideline 3.1.1).
      setError(nativeSafeErrorMessage(err, 'Failed to add league. Check your details and try again.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50 px-4" style={{ paddingTop: 'calc(var(--safe-top) + 20px)', paddingBottom: 'calc(var(--safe-bottom) + 20px)' }}>
      {/* A firm max-height (not just "whatever fits") so this reads as a
          centered dialog with real margin above and below, even for ESPN's
          taller handoff/manual-entry content — instead of ballooning to fill
          almost the entire screen. The .overflow-y-auto body below does the
          actual scrolling once content exceeds this. */}
      <div className="bg-white border rounded-2xl w-full max-w-lg shadow-2xl flex flex-col" style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderColor: JAI_COLORS.hair, maxHeight: 'min(640px, 82dvh)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
          <div className="flex items-center gap-2.5">
            <JaiMark size={28} />
            <h2 style={{ fontFamily: JAI_FONTS.tight, fontSize: 18, fontWeight: 600, color: JAI_COLORS.navy }}>Connect Your League</h2>
          </div>
          <button onClick={onClose} style={{ color: JAI_COLORS.mute, fontSize: 28, lineHeight: 1, transition: 'color 0.2s', cursor: 'pointer', background: 'none', border: 'none' }} onMouseEnter={e => e.target.style.color = JAI_COLORS.navy} onMouseLeave={e => e.target.style.color = JAI_COLORS.mute}>×</button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>

        {/* Platform selector */}
        <div className="px-6 pt-5">
          <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">Platform</p>
          <div className="flex flex-col gap-2">
            {PLATFORMS.map(p => {
              const selected = platform === p.id
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setPlatform(p.id); setError('') }}
                  className="flex items-center justify-between gap-3 rounded-xl text-left transition"
                  style={{
                    padding: selected ? '13.5px 17px' : '15px 18px',
                    border: selected ? `2px solid ${JAI_COLORS.blue}` : `1.5px solid ${JAI_COLORS.hair}`,
                    background: JAI_COLORS.white,
                  }}
                >
                  <span>
                    <span
                      className="block"
                      style={{ fontFamily: JAI_FONTS.tight, fontSize: 16, fontWeight: 700, letterSpacing: '-0.005em', color: JAI_COLORS.navy }}
                    >
                      {p.label}
                    </span>
                    <span className="block mt-0.5" style={{ fontSize: 12.5, color: JAI_COLORS.mute }}>
                      {p.description}
                    </span>
                  </span>
                  <span
                    className="flex-shrink-0 rounded-full flex items-center justify-center"
                    style={{
                      width: 19, height: 19,
                      border: selected ? `2px solid ${JAI_COLORS.blue}` : `1.5px solid ${JAI_COLORS.hair}`,
                    }}
                  >
                    {selected && (
                      <span className="rounded-full" style={{ width: 9, height: 9, background: JAI_COLORS.blue }} />
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-4 space-y-4">
          {error && (
            <div className="text-sm text-gray-900" style={{ borderLeft: '3px solid #D63A3A', paddingLeft: 12 }}>
              {error}
            </div>
          )}

          {/* ── ESPN ────────────────────────────────────────────── */}
          {platform === 'espn' && IS_NATIVE_APP && <NativeEspnHandoff />}
          {platform === 'espn' && !IS_NATIVE_APP && (
            <>
              {espnStep !== 'ready' && (
                <div className="rounded-xl p-4 space-y-3" style={{ background: JAI_COLORS.navy }}>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    ESPN doesn’t offer a sign-in button to other apps, so JAI needs the
                    login your browser already holds.
                    {espnExtension
                      ? ' The JAI extension can hand it over in one click.'
                      : espnExtensionUrl
                        ? ' Two ways to hand it over:'
                        : ' Here’s how to copy it across — it takes about a minute.'}
                  </p>

                  {/* One click, if the extension is installed. */}
                  {espnExtension ? (
                    <button
                      type="button"
                      onClick={handleExtensionConnect}
                      disabled={!espnToken || espnStep === 'waiting'}
                      className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-lg px-4 py-2.5 disabled:opacity-60"
                      style={{ background: `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, ${JAI_COLORS.blueBright} 100%)`, boxShadow: `0 4px 14px ${JAI_COLORS.blue}40` }}
                    >
                      {espnStep === 'waiting' ? 'Connecting…' : 'Connect with the JAI extension'}
                    </button>
                  ) : espnExtensionUrl ? (
                    <div className="space-y-2">
                      <a
                        href={espnExtensionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-lg px-4 py-2.5"
                        style={{ background: `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, ${JAI_COLORS.blueBright} 100%)`, boxShadow: `0 4px 14px ${JAI_COLORS.blue}40` }}
                      >
                        Install the JAI extension — one click after that
                      </a>
                      <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                        Already installed? Reload this page so JAI can see it.
                      </p>
                    </div>
                  ) : null}

                  {espnStep === 'waiting' && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: JAI_COLORS.blueBright }}></span>
                      Reading your ESPN sign-in…
                    </div>
                  )}

                  {/* Always available, and the only option on mobile or without
                      the extension. Collapsed by default so it doesn't compete
                      with the one-click path for users who do have it. */}
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => setEspnManualOpen(o => !o)}
                      className="text-xs font-medium underline"
                      style={{ color: JAI_COLORS.blueBright }}
                    >
                      {espnManualOpen ? 'Hide manual setup' : espnExtension ? 'Or enter it manually' : 'Enter it manually instead (no install)'}
                    </button>

                    {espnManualOpen && (
                      <div className="mt-3 space-y-2.5">
                        <ol className="text-[11.5px] space-y-1.5 pl-4 list-decimal" style={{ color: 'rgba(255,255,255,0.8)' }}>
                          <li>Open <strong style={{ color: JAI_COLORS.white }}>fantasy.espn.com</strong> and sign in.</li>
                          <li>Right-click the page → <strong style={{ color: JAI_COLORS.white }}>Inspect</strong>.</li>
                          <li>Go to the <strong style={{ color: JAI_COLORS.white }}>Application</strong> tab (Firefox calls it <strong style={{ color: JAI_COLORS.white }}>Storage</strong>).</li>
                          <li>In the sidebar: <strong style={{ color: JAI_COLORS.white }}>Cookies → https://fantasy.espn.com</strong>.</li>
                          <li>Find <code style={{ fontFamily: JAI_FONTS.mono, color: JAI_COLORS.white }}>espn_s2</code> and <code style={{ fontFamily: JAI_FONTS.mono, color: JAI_COLORS.white }}>SWID</code>, and copy each Value below.</li>
                        </ol>

                        <div>
                          <label className="block text-[11px] mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>espn_s2</label>
                          <input
                            type="text"
                            value={manualS2}
                            onChange={e => setManualS2(e.target.value)}
                            placeholder="AEB7x...(long string)"
                            spellCheck="false"
                            autoComplete="off"
                            className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: JAI_COLORS.white, fontFamily: JAI_FONTS.mono }}
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>SWID</label>
                          <input
                            type="text"
                            value={manualSwid}
                            onChange={e => setManualSwid(e.target.value)}
                            placeholder="{XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}"
                            spellCheck="false"
                            autoComplete="off"
                            className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: JAI_COLORS.white, fontFamily: JAI_FONTS.mono }}
                          />
                        </div>

                        <button
                          type="button"
                          onClick={handleManualConnect}
                          className="w-full text-xs font-semibold text-white rounded-lg px-4 py-2"
                          style={{ background: JAI_COLORS.blue }}
                        >
                          Use these values
                        </button>
                      </div>
                    )}
                  </div>

                  {espnStep === 'expired' && (
                    <button type="button" onClick={restartEspnConnect} className="text-xs font-medium underline" style={{ color: JAI_COLORS.blueBright }}>
                      Start over
                    </button>
                  )}
                </div>
              )}

              {espnStep === 'ready' && (
                <>
                  {/* The extension path proves the credentials came from a live
                      ESPN session. A manual paste proves nothing until ESPN is
                      actually asked, which happens on submit — so don't claim
                      "connected" for something that might be a typo. */}
                  <div
                    className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium"
                    style={{ background: JAI_COLORS.navy, color: JAI_COLORS.white, fontFamily: JAI_FONTS.tight }}
                  >
                    <span
                      className="flex-shrink-0 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center"
                      style={{ background: JAI_COLORS.success }}
                    >✓</span>
                    {espnSource === 'manual' ? 'ESPN details saved' : 'Connected to ESPN'}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">ESPN League ID</label>
                    <input
                      type="text"
                      value={espnLeagueId}
                      onChange={e => setEspnLeagueId(e.target.value)}
                      required
                      placeholder="e.g. 12345678"
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      Found in your ESPN league URL: <code className="text-gray-700">...?leagueId=XXXXXXXX</code>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Season (optional)</label>
                    <input
                      type="number"
                      value={season}
                      onChange={e => setSeason(e.target.value)}
                      onWheel={e => e.target.blur()}
                      min="2020" max="2030"
                      placeholder="Current season"
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />
                    <p className="text-gray-500 text-xs mt-1">Leave blank to use the current season — JAI works this out from your league.</p>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Sleeper ──────────────────────────────────────────── */}
          {platform === 'sleeper' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sleeper League ID</label>
                <input
                  type="text"
                  value={sleeperLeagueId}
                  onChange={e => setSleeperLeagueId(e.target.value)}
                  required
                  placeholder="e.g. 1048960940595920896"
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                />
                <p className="text-gray-500 text-xs mt-1">
                  On desktop: open your league on Sleeper.com and copy the number from the URL. On mobile: open your league → Settings → scroll to find the League ID.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Sleeper Username</label>
                <input
                  type="text"
                  value={sleeperUsername}
                  onChange={e => setSleeperUsername(e.target.value)}
                  placeholder="your_sleeper_username"
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Used to identify which team is yours. Find it in your Sleeper profile.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Season (optional)</label>
                <input
                  type="number"
                  value={season}
                  onChange={e => setSeason(e.target.value)}
                  onWheel={e => e.target.blur()}
                  min="2020" max="2030"
                  placeholder="Current season"
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                />
                <p className="text-gray-500 text-xs mt-1">Leave blank to use the current season — JAI works this out from your league.</p>
              </div>

              <div className="rounded-xl px-4 py-3" style={{ background: JAI_COLORS.navy }}>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.86)' }}>
                  <strong style={{ color: JAI_COLORS.white }}>No login required</strong> — Sleeper's data is public, so your league ID is all JAI needs.
                </p>
              </div>
            </>
          )}

          {/* ── Yahoo ────────────────────────────────────────────── */}
          {platform === 'yahoo' && (
            <>
              {yahooStep === 'connect' && yahooReady === null && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <span className="animate-spin inline-block text-2xl">⟳</span>
                </div>
              )}

              {yahooStep === 'connect' && yahooReady === false && (
                <div className="rounded-xl p-5 text-center" style={{ background: JAI_COLORS.paper, border: `1px solid ${JAI_COLORS.hair}` }}>
                  <p className="text-sm font-semibold mb-1.5" style={{ color: JAI_COLORS.navy, fontFamily: JAI_FONTS.tight }}>
                    Yahoo leagues are coming soon
                  </p>
                  <p className="text-sm" style={{ color: JAI_COLORS.navy, opacity: 0.72 }}>
                    We're waiting on Yahoo to approve JAI's access to their Fantasy API.
                    In the meantime you can connect an ESPN or Sleeper league.
                  </p>
                </div>
              )}

              {yahooStep === 'connect' && yahooReady === true && (
                <div className="space-y-4">
                  <div className="rounded-xl p-4 text-center" style={{ background: JAI_COLORS.navy }}>
                    <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.86)' }}>
                      Yahoo requires you to log in through Yahoo's website to grant access.
                      You'll be redirected briefly, then brought back here.
                    </p>
                    <button
                      type="button"
                      onClick={handleYahooConnect}
                      disabled={yahooLoading}
                      className="inline-flex items-center gap-2 text-white font-semibold rounded-xl px-6 py-2.5 text-sm transition"
                      style={{
                        background: yahooLoading ? JAI_COLORS.blue + '80' : `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, ${JAI_COLORS.blueBright} 100%)`,
                        boxShadow: yahooLoading ? 'none' : `0 4px 14px ${JAI_COLORS.blue}40`,
                        cursor: yahooLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {yahooLoading && <span className="animate-spin text-base">⟳</span>}
                      {yahooLoading ? 'Redirecting…' : 'Connect with Yahoo'}
                    </button>
                  </div>
                </div>
              )}

              {yahooStep === 'pick' && (
                <div className="space-y-3">
                  {yahooLoading ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      <span className="animate-spin inline-block text-2xl">⟳</span>
                      <p className="mt-2">Loading your Yahoo leagues…</p>
                    </div>
                  ) : yahooLeagues.length === 0 ? (
                    <div className="text-sm text-gray-900" style={{ borderLeft: '3px solid #9A6A00', paddingLeft: 12 }}>
                      No active Yahoo Fantasy leagues found. Make sure you're in a league this season.
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">Select the league you want to import:</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {yahooLeagues.map(league => (
                          <button
                            key={league.leagueKey}
                            type="button"
                            onClick={() => setYahooSelected(league)}
                            className={`w-full text-left px-4 py-3 rounded-xl border-2 transition ${
                              yahooSelected?.leagueKey === league.leagueKey
                                ? 'border-blue-500 bg-white'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <p className="font-medium text-gray-900 text-sm">{league.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {league.sport?.toUpperCase()} · {league.teamCount} teams · {league.season} season
                            </p>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* Actions */}
          {/* Yahoo shows the full row only after picking a league; ESPN only once the credentials are in hand */}
          {(platform !== 'yahoo' || yahooStep === 'pick') && (platform !== 'espn' || espnStep === 'ready') && (
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                style={{ background: JAI_COLORS.paper, color: JAI_COLORS.navy, border: `1px solid ${JAI_COLORS.hair}`, fontWeight: 500, fontSize: 13, borderRadius: 8, padding: '10px 16px', cursor: 'pointer', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight }}
                onMouseEnter={(e) => { e.target.style.background = JAI_COLORS.hair }}
                onMouseLeave={(e) => { e.target.style.background = JAI_COLORS.paper }}
                className="flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || (platform === 'yahoo' && !yahooSelected)}
                style={{ background: loading ? JAI_COLORS.blue + '80' : `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, #1E5BD6 100%)`, color: 'white', fontWeight: 600, fontSize: 13, borderRadius: 8, padding: '10px 16px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight, boxShadow: loading ? 'none' : `0 4px 16px ${JAI_COLORS.blue}40` }}
                onMouseEnter={(e) => { if (!loading && !(platform === 'yahoo' && !yahooSelected)) { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = `0 6px 20px ${JAI_COLORS.blue}50` } }}
                onMouseLeave={(e) => { if (!loading && !(platform === 'yahoo' && !yahooSelected)) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = `0 4px 16px ${JAI_COLORS.blue}40` } }}
                className="flex-1"
              >
                {loading ? 'Connecting…' : 'Connect League'}
              </button>
            </div>
          )}

          {/* Show cancel only while Yahoo/ESPN connect is still in progress */}
          {((platform === 'yahoo' && yahooStep === 'connect') || (platform === 'espn' && espnStep !== 'ready')) && (
            <div className="pt-1">
              <button
                type="button"
                onClick={onClose}
                style={{ width: '100%', background: JAI_COLORS.paper, color: JAI_COLORS.navy, border: `1px solid ${JAI_COLORS.hair}`, fontWeight: 500, fontSize: 13, borderRadius: 8, padding: '10px 16px', cursor: 'pointer', transition: 'all 0.2s', fontFamily: JAI_FONTS.tight }}
                onMouseEnter={(e) => { e.target.style.background = JAI_COLORS.hair }}
                onMouseLeave={(e) => { e.target.style.background = JAI_COLORS.paper }}
              >
                Cancel
              </button>
            </div>
          )}
        </form>
        </div>
      </div>
    </div>
  )
}
